import { api } from '@/lib/api/client';

/**
 * PRD §12 — Reporter admin API (brand-manager-facing).
 *
 * Mirrors the BE controller at `/co-reporter`. All endpoints require auth.
 * The public verify path lives in `./api.ts` and uses publicClient.
 */

export interface ReporterListRow {
  id: number;
  name: string;
  email: string;
  linkState: 'active' | 'revoked' | 'expired';
  expiresAt: string;
  issuedAt: string;
  termsAccepted: boolean;
}

/**
 * Brand-wide distinct reporter (one row per email across all the brand's
 * events). Adds the source `eventId` of the most recent invite so the Planner
 * can show "last invited to …" context.
 */
export interface BrandReporterRow extends ReporterListRow {
  eventId: number;
}

export interface InviteResult {
  reporterId: number;
  email: string;
  name: string | null;
  token: string;
  magicUrl: string;
  expiresAt: string;
}

export interface ReissueResult {
  token: string;
  magicUrl: string;
  expiresAt: string;
}

export const reporterAdminApi = {
  invite: (input: {
    eventId: number;
    email: string;
    name?: string;
    phone?: string;
    brandId?: number;
  }) => api.post<InviteResult>('/co-reporter/invite', input),

  listForEvent: (eventId: number) =>
    api.get<ReporterListRow[]>(`/co-reporter/event/${eventId}`),

  /**
   * Distinct reporters across all of a brand's events (de-duped by email).
   * Powers the Planner reporters panel. 403 if the brand isn't accessible.
   */
  listForBrand: (brandId: number) =>
    api.get<BrandReporterRow[]>(`/co-reporter/brand/${brandId}`),

  reissue: (reporterId: number) =>
    api.post<ReissueResult>(`/co-reporter/${reporterId}/reissue`),

  revoke: (reporterId: number) =>
    api.delete<{ ok: true }>(`/co-reporter/${reporterId}/revoke`),

  /** Remove the reporter from the event entirely (deletes row + links). */
  remove: (reporterId: number) =>
    api.delete<{ ok: true }>(`/co-reporter/${reporterId}`),
};
